import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth/types';

import useHasPermissions from 'src/hooks/useHasPermissions';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useTitleStore } from 'src/stores/useTitleStore';
import { billingMessages } from 'src/features/billingEntity/utils/messages';
import { useListBillingEntities } from 'src/features/billingEntity/api/useListBillingEntities';
import { BillingEntity } from 'src/features/billingEntity/utils/types';
import { entityMessages } from 'src/features/entity/utils/messages';
import CellBillingEntityActions from 'src/features/billingEntity/components/tables/CellBillingEntityActions';
import { useParking } from 'src/providers/ParkingProvider';

const BillingEntitiesTable = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(billingMessages.billingEntities));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const manageBillingEntity = useHasPermissions([PERMISSION.MANAGE_SERVICE_PROVIDERS]);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const {
        data: billingEntities,
        isError,
        isLoading,
    } = useListBillingEntities({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
    });

    const columnHelper = createColumnHelper<BillingEntity>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(entityMessages.name),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(entityMessages.email),
            },
        }),
        columnHelper.accessor('uic', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(billingMessages.legalNumber),
            },
        }),
        columnHelper.accessor('tax_registration_number', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(billingMessages.taxNumber),
            },
        }),
        ...(manageBillingEntity
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellBillingEntityActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<BillingEntity, any>
            columns={columns}
            data={billingEntities}
            pagination={pagination}
            search={search}
            semantics={{
                title: formatMessage(billingMessages.billingEntities),
                entryName: formatMessage(billingMessages.billingEntity).toLowerCase(),
                entryNames: formatMessage(billingMessages.billingEntities).toLowerCase(),
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default BillingEntitiesTable;
